import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { getCatalog, getPunkts, addAssayToOrderList, removeAssayFromOrderList } from '@cabinet-packages/redux';
import { useHistory, useLocation } from 'react-router';
import { Box, Typography } from '@material-ui/core';
import { getAssayInfo } from '@cabinet-packages/api';
import { Helmet } from 'react-helmet';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCartOutlined';
import DeleteIcon from '@material-ui/icons/Delete';
import Button from '../../components/Button/Button';
import ScrollContainer from '../../components/ScrollContainer/ScrollContainer';
import Loading from '../Catalog/components/Loading';
import { designTokens } from '../../styles/designTokens';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const useStyles = makeStyles(() => ({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  backButtonWrapper: {
    padding: `${designTokens.spacing.lg} ${designTokens.spacing.lg} ${designTokens.spacing.md}`,
    '@media (max-width: 640px)': {
      padding: `${designTokens.spacing.md} ${designTokens.spacing.sm} ${designTokens.spacing.sm}`,
    },
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    color: designTokens.colors.text.primary,
    fontSize: 24,
    cursor: 'pointer',
    '&:hover': {
      color: designTokens.colors.primary,
    },
  },
  backButtonText: {
    ...designTokens.typography.body,
    fontSize: '14px',
    lineHeight: '100%',
    '&:hover': {
      color: designTokens.colors.primary,
    },
  },
  scrollableContent: {
    flex: 1,
    overflow: 'hidden',
  },
  contentInner: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    '@media (max-width: 1240px)': {
      minWidth: 'auto',
    },
  },
  researchHeaderWrapper: {
    padding: `0 ${designTokens.spacing.lg} ${designTokens.spacing.lg}`,
    '@media (max-width: 640px)': {
      padding: `0 ${designTokens.spacing.sm} ${designTokens.spacing.md}`,
    },
  },
  researchHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: designTokens.spacing.lg,
    flexWrap: 'wrap',
    maxWidth: '1200px',
    margin: '0 auto',
    '@media (max-width: 900px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: designTokens.spacing.md,
    },
  },
  titleSection: {
    flex: 1,
    minWidth: '300px',
  },
  researchTitle: {
    ...designTokens.typography.h1,
  },
  priceSection: {
    display: 'flex',
    alignItems: 'center',
    gap: designTokens.spacing.lg,
    flexShrink: 0,
    '@media (max-width: 600px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: designTokens.spacing.md,
      width: '100%',
    },
  },
  priceInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: designTokens.spacing.lg,
    '@media (max-width: 600px)': {
      gap: designTokens.spacing.md,
    },
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.md,
  },
  price: {
    ...designTokens.typography.h4,
    whiteSpace: 'nowrap',
  },
  oldPrice: {
    ...designTokens.typography.h4,
    fontWeight: 400,
    textDecoration: 'line-through',
    whiteSpace: 'nowrap',
  },
  duration: {
    display: 'flex',
    alignItems: 'center',
    gap: designTokens.spacing.xs,
    color: designTokens.colors.text.secondary,
    whiteSpace: 'nowrap',
  },
  durationIcon: {
    fontSize: '20px',
    color: designTokens.colors.grey[400],
  },
  durationText: {
    ...designTokens.typography.label,
    color: designTokens.colors.text.secondary,
  },
  addToCartWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
  },
  addToCartButton: {
    minWidth: '180px',
    '@media (max-width: 600px)': {
      width: '100%',
    },
  },
  section: {
    marginBottom: designTokens.spacing.xl,
  },
  sectionTitle: {
    ...designTokens.typography.h2,
    marginBottom: designTokens.spacing.md,
  },
  sectionContent: {
    ...designTokens.typography.body,
    fontWeight: 400,
    color: designTokens.colors.text.primary,
    '& *': {
      fontFamily: 'inherit',
      fontSize: 'inherit',
      color: 'inherit',
    },
    '& p': {
      marginBottom: designTokens.spacing.md,
      lineHeight: '1.6',
    },
    '& span': {
      lineHeight: '1.6',
    },
    '& ul, & ol': {
      paddingLeft: '24px',
      marginBottom: designTokens.spacing.md,
      listStyle: 'square',
    },
    '& li': {
      marginBottom: designTokens.spacing.sm,
      lineHeight: '1.6',
    },
    '& strong, & b': {
      fontWeight: 500,
    },
  },
  researchList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  researchItem: {
    padding: `${designTokens.spacing.sm} 0`,
    borderBottom: `1px solid ${designTokens.colors.border}`,
    ...designTokens.typography.body,
    fontWeight: 400,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  errorMessage: {
    ...designTokens.typography.body,
    color: designTokens.colors.red.main,
    textAlign: 'center',
    padding: designTokens.spacing.xl,
  },
}));

const ResearchDetails = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { catalog, complexResearch, takeId } = useSelector(state => state.assaysCatalog);
  const { orderingList } = useSelector(state => state.orderAssays);
  const { punkts } = useSelector(store => store.collectionPoints);

  const [item, setItem] = useState(null);
  const [assayInfo, setAssayInfo] = useState(null);
  const [loadingItem, setLoadingItem] = useState(true);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState(null);

  const isLoading = loadingItem || loadingInfo;

  const locationId = [...location.pathname.split('/')].pop();
  const hrefLocation = window.location.href;

  const normalizeType = (type) => {
    if (type === 'packet' || type === 'complex') return 'complex';
    return type;
  };

  const isIncludedInOrderingList = item && orderingList?.find(
    assay => assay && +assay.id === +item.id && normalizeType(assay.type) === normalizeType(item.type)
  );

  const isComplex = item?.superGroupName || item?.filling?.length > 0 || item?.idGrTest?.length > 0;

  const parseIdGrTest = (idGrTest) => {
    if (!idGrTest) return [];
    if (Array.isArray(idGrTest)) return idGrTest;
    if (typeof idGrTest === 'string') {
      try {
        return JSON.parse(idGrTest);
      } catch {
        return [];
      }
    }
    return [];
  };

  const researchItems = [
    ...(item?.filling || []),
    ...parseIdGrTest(item?.idGrTest)
  ].filter(research => research && research.name);

  const formatDuration = (item) => {
    if (!item) return '';
    if (item.duration_day && item.duration_day >= 1) {
      return `${item.duration_day} д.`;
    }
    if (item.duration_min && item.duration_min > 0) {
      return `${item.duration_min} хв.`;
    }
    return '';
  };

  const calculatePrice = (item) => {
    if (!item) return 0;
    if (item.discount) {
      return (item.price - (item.price * (item.discount / 100))).toFixed(2);
    }
    return item.price;
  };

  const getName = (item) => {
    if (!item) return '';
    return item.name || (item.testNames ? item.testNames.split(',')[0].split('\\\\').join('') : '');
  };

  const handleBack = () => {
    history.push('/catalog');
  };

  const getCartPrice = () => {
    if (!item) return 0;
    const isDoctor = localStorage.getItem('isDoctor') || process.env.REACT_APP_PROJECT_NAME === 'CALCULATOR';
    return item.discount && !isDoctor
      ? item.price - item.price * item.discount / 100
      : item.price;
  };

  const handleAddToCart = () => {
    if (!item || isIncludedInOrderingList) return;

    dispatch(addAssayToOrderList(
      item.id,
      getCartPrice(),
      () => {},
      item.type,
    ));
  };

  const handleRemoveFromCart = () => {
    if (!item || !isIncludedInOrderingList) return;

    dispatch(removeAssayFromOrderList(
      item.id,
      getCartPrice(),
      () => {},
      item.type,
    ));
  };

  const handleAssayInfo = useCallback(async (id) => {
    try {
      setLoadingInfo(true);
      const response = await getAssayInfo(id);
      setAssayInfo(response?.info || response);
    } catch (err) {
      console.error('Error loading assay info:', err);
    } finally {
      setLoadingInfo(false);
    }
  }, []);

  useEffect(() => {
    if (!catalog && takeId) {
      dispatch(getCatalog());
    }

    if (catalog && complexResearch && locationId) {
      const foundItem = [...(catalog || []), ...(complexResearch || [])].find(
        (i) => !i.superGroupName && +i.id === +locationId
      );

      if (foundItem) {
        setItem(foundItem);
        handleAssayInfo(foundItem.id);
        setLoadingItem(false);
      } else {
        setError('Дослідження не знайдено');
        setLoadingItem(false);
        setLoadingInfo(false);
      }
    }
  }, [catalog, complexResearch, dispatch, locationId, handleAssayInfo, takeId]);

  useEffect(() => {
    if (!punkts) {
      dispatch(getPunkts());
    }
  }, [dispatch, punkts]);

  const renderSection = (title, content) => {
    if (!content) return null;
    return (
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>{title}</Typography>
        <Box
          className={classes.sectionContent}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
        />
      </Box>
    );
  };

  const renderResearchList = () => {
    if (!isComplex || researchItems.length === 0) return null;
    return (
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Дослідження що входять в комплекс:
        </Typography>
        <ul className={classes.researchList}>
          {researchItems.map((research, index) => (
            <li key={research.id || index} className={classes.researchItem}>
              {research.name}
            </li>
          ))}
        </ul>
      </Box>
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Box className={classes.pageContainer}>
        <Box className={classes.backButtonWrapper}>
          <Box className={classes.headerContainer}>
            <Box className={classes.backButton} onClick={handleBack}>
              <ArrowBackIcon />
              <Typography className={classes.backButtonText}>Повернутись назад</Typography>
            </Box>
          </Box>
        </Box>
        <Typography className={classes.errorMessage}>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.pageContainer}>
      <Helmet>
        <meta property="og:title" content={getName(item)} />
        <meta property="og:url" content={hrefLocation} />
        <meta property="og:description" content={getName(item)} />
        <meta property="og:site_name" content="Esculab" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Box className={classes.backButtonWrapper}>
        <Box className={classes.headerContainer}>
          <Box className={classes.backButton} onClick={handleBack}>
            <ArrowBackIcon />
            <Typography className={classes.backButtonText}>Повернутись назад</Typography>
          </Box>
        </Box>
      </Box>

      <Box className={classes.researchHeaderWrapper}>
        <Box className={classes.researchHeader}>
          <Box className={classes.titleSection}>
            <Typography className={classes.researchTitle}>
              {getName(item)}
            </Typography>
          </Box>

          <Box className={classes.priceSection}>
            <Box className={classes.priceInfo}>
              <Box className={classes.priceContainer}>
                <Typography className={classes.price}>
                  {calculatePrice(item)} грн
                </Typography>
                {Number(item?.discount) > 0 && Number(item?.price) > 0 && (
                  <Typography className={classes.oldPrice}>
                    {item.price} грн
                  </Typography>
                )}
              </Box>

              {formatDuration(item) && (
                <Box className={classes.duration}>
                  <AccessTimeIcon className={classes.durationIcon} />
                  <Typography className={classes.durationText}>
                    {formatDuration(item)}
                  </Typography>
                </Box>
              )}
            </Box>

            {!!item?.price && (
              <Box className={classes.addToCartWrapper}>
                <Box className={classes.addToCartButton}>
                  {isIncludedInOrderingList ? (
                    <Button
                      variant="danger"
                      icon={<DeleteIcon />}
                      iconPosition="left"
                      size="small"
                      onClick={handleRemoveFromCart}
                    >
                      Видалити з кошика
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      icon={<ShoppingCartIcon />}
                      iconPosition="left"
                      size="small"
                      onClick={handleAddToCart}
                    >
                      Додати в кошик
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box className={classes.scrollableContent}>
        <ScrollContainer>
          <Box className={classes.contentInner}>
            {renderSection('Визначення:', assayInfo?.ua_definition)}
            {renderSection('Загальні характеристики:', assayInfo?.ua_general_characteristics)}
            {renderSection('Показання до призначення:', assayInfo?.ua_doctor_rules)}
            {renderSection('Синоніми:', assayInfo?.ua_synonyms)}
            {renderSection('Правила підготовки:', assayInfo?.ua_rules || assayInfo?.ua_total_rules)}
            {renderSection('Інтерпретація підвищених значень:', assayInfo?.ua_interpretation_enhanced)}
            {renderSection('Інтерпретація знижених значень:', assayInfo?.ua_interpretation_reduced)}

            {renderResearchList()}
          </Box>
        </ScrollContainer>
      </Box>
    </Box>
  );
};

export default ResearchDetails;
